import { useId, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { formatUsPhone } from "@customer/Marketing/_helpers/formatUsPhone";
import type { OrganizationFormValues } from "../_helpers/organizationForm";
import { AddressFormFields } from "./AddressFormFields";
import { FormField, INPUT_CLASS } from "./FormField";

interface OrganizationFormFieldsProps {
    // The New Client drawer's Tax ID field, shown under the legal name. The
    // tax id is not editable (C5), so the edit drawer passes none.
    taxIdField?: ReactNode;
}

// The organization form's body (C5): name, legal name, the address as
// separate fields and the contact. It renders inside a react-hook-form
// FormProvider over OrganizationFormValues, so the Edit Organization drawer
// and the New Client drawer (C3/C4 Lead call) each own their form, submit
// and outcome around the same fields. The address fields are the Edit
// Location drawer's too.
export const OrganizationFormFields = ({
    taxIdField,
}: OrganizationFormFieldsProps) => {
    const {
        register,
        setValue,
        formState: { errors },
    } = useFormContext<OrganizationFormValues>();
    const id = useId();
    const fieldId = (name: keyof OrganizationFormValues) => `${id}-${name}`;

    return (
        <div className="flex flex-col gap-4">
            <FormField
                id={fieldId("name")}
                label="Org Name"
                error={errors.name?.message}
            >
                <input
                    id={fieldId("name")}
                    type="text"
                    className={INPUT_CLASS}
                    {...register("name")}
                />
            </FormField>
            <FormField
                id={fieldId("legalBusinessName")}
                label="Legal Business Name"
                error={errors.legalBusinessName?.message}
            >
                <input
                    id={fieldId("legalBusinessName")}
                    type="text"
                    className={INPUT_CLASS}
                    {...register("legalBusinessName")}
                />
            </FormField>
            {taxIdField}

            <AddressFormFields />

            <fieldset className="flex flex-col gap-4">
                <legend className="mb-2 font-bold">Contact</legend>
                <FormField
                    id={fieldId("firstName")}
                    label="First Name"
                    error={errors.firstName?.message}
                >
                    <input
                        id={fieldId("firstName")}
                        type="text"
                        className={INPUT_CLASS}
                        {...register("firstName")}
                    />
                </FormField>
                <FormField
                    id={fieldId("lastName")}
                    label="Last Name"
                    error={errors.lastName?.message}
                >
                    <input
                        id={fieldId("lastName")}
                        type="text"
                        className={INPUT_CLASS}
                        {...register("lastName")}
                    />
                </FormField>
                <FormField
                    id={fieldId("email")}
                    label="Email"
                    error={errors.email?.message}
                >
                    <input
                        id={fieldId("email")}
                        type="email"
                        className={INPUT_CLASS}
                        {...register("email")}
                    />
                </FormField>
                <FormField
                    id={fieldId("phoneNumber")}
                    label="Phone"
                    error={errors.phoneNumber?.message}
                >
                    {/* Masked as typed, as the apply and profile forms are. */}
                    <input
                        id={fieldId("phoneNumber")}
                        type="tel"
                        inputMode="numeric"
                        placeholder="(___) ___-____"
                        className={INPUT_CLASS}
                        {...register("phoneNumber")}
                        onChange={(event) =>
                            setValue(
                                "phoneNumber",
                                formatUsPhone(event.target.value),
                                { shouldDirty: true, shouldValidate: true },
                            )
                        }
                    />
                </FormField>
            </fieldset>
        </div>
    );
};
